import { useEffect, useState } from "react";
import { api, type OpsStatus } from "../lib/api";
import { duration, pct } from "../lib/format";

/**
 * Rytmus sběru.
 *
 * Bez tohohle vypadá prázdný dashboard jako porucha. Přitom mezi koly soutěže
 * se legitimně nesbírá klidně dva týdny: plánovač začíná sedm dní před výkopem
 * a do té doby nemá co dělat ani co utrácet.
 */
function Rhythm({ data }: { data: OpsStatus }) {
  const now = Date.now();
  const kickoff = data.nextKickoff ? new Date(data.nextKickoff) : null;
  const next = data.units
    .map((u) => (u.next_capture_at ? new Date(u.next_capture_at) : null))
    .filter((d): d is Date => d !== null)
    .sort((a, b) => a.getTime() - b.getTime())[0];

  if (!kickoff) {
    return (
      <p className="notice">
        V rozpisu není žádný nadcházející zápas. Rozpis se obnovuje každých patnáct minut
        a je zdarma, takže se naplní sám, jakmile ho zdroj zveřejní.
      </p>
    );
  }

  const waiting = next !== undefined && next.getTime() - now > 3_600_000;

  return (
    <>
      <dl className="readout" style={{ borderBottom: "none" }}>
        <div>
          <dt>Nejbližší výkop</dt>
          <dd className="num">za {duration(kickoff.getTime() - now)}</dd>
        </div>
        <div>
          <dt>Další odečet</dt>
          <dd className="num">
            {next === undefined ? "žádný" : next.getTime() <= now ? "teď" : `za ${duration(next.getTime() - now)}`}
          </dd>
        </div>
        <div>
          <dt>Zápasů v rozpisu</dt>
          <dd className="num">{data.upcomingEvents}</dd>
        </div>
      </dl>

      {waiting && (
        <p className="notice">
          Do dalšího odečtu se nic nestane a neutratí se žádný kredit. Sběr začíná sedm
          dní před výkopem, takže mezi koly soutěže je pauza normální stav, ne porucha.
        </p>
      )}
    </>
  );
}

export function Ops() {
  const [data, setData] = useState<OpsStatus | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = () => api.ops().then(setData).catch((e: Error) => setError(e.message));
    void load();
    const timer = setInterval(load, 30_000);
    return () => clearInterval(timer);
  }, []);

  if (error) return <p className="notice warn">{error}</p>;
  if (!data) return <p className="empty">Načítám.</p>;

  const limit = data.quota[0]?.monthly_credit_limit ?? 0;
  const spent = data.quota.reduce((sum, row) => sum + Number(row.credits_spent), 0);
  const used = limit === 0 ? 0 : spent / limit;

  return (
    <>
      <h1>Provoz</h1>
      <p className="lede">
        Volný tarif utáhne jednu ligu a jeden trh. Rezerva 40 % je vyhrazená zavíracímu oknu
        a běžný sběr na ni nesmí sáhnout.
      </p>

      <dl className="readout">
        <div>
          <dt>Kvóta tento měsíc</dt>
          <dd className="num">{spent} / {limit}</dd>
        </div>
        <div>
          <dt>Vyčerpáno</dt>
          <dd className={`num ${used >= 0.8 ? "neg" : ""}`}>{pct(used, 0)}</dd>
        </div>
        <div>
          <dt>Odečtů za 24 h</dt>
          <dd className="num">{data.snapshotsLast24h}</dd>
        </div>
        <div>
          <dt>Čeká na potvrzení</dt>
          <dd className="num">{data.openReviewItems}</dd>
        </div>
      </dl>

      <Rhythm data={data} />

      {used >= 0.8 && (
        <p className="notice warn">
          Kvóta je nad 80 %. Běžný sběr se brzy degraduje na režim jen zavírací okno. Uber
          kadenci v okně T-24 h, to je hlavní páka na úsporu.
        </p>
      )}

      <h2>Spotřeba po bucketech</h2>
      <div className="scroll-x">
        <table>
          <thead>
            <tr><th>Zdroj</th><th>Bucket</th><th className="right">Kredity</th></tr>
          </thead>
          <tbody>
            {data.quota.map((row) => (
              <tr key={`${row.provider}-${row.bucket}`}>
                <td>{row.provider}</td>
                <td>{row.bucket === "closing" ? "zavírací okno" : "běžný sběr"}</td>
                <td className="right num">{row.credits_spent}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2>Sběrné jednotky</h2>
      <div className="scroll-x">
        <table>
          <thead>
            <tr>
              <th>Liga</th><th>Trh</th><th>Poslední odečet</th>
              <th className="right">Selhání za sebou</th><th>Stav</th>
            </tr>
          </thead>
          <tbody>
            {data.units.map((unit) => {
              const age = unit.last_captured_at
                ? Date.now() - new Date(unit.last_captured_at).getTime()
                : null;
              const stale = age !== null && age > 3 * 3_600_000;
              return (
                <tr key={`${unit.league}-${unit.market_code}`}>
                  <td>{unit.league}</td>
                  <td>{unit.market_code}</td>
                  <td className={stale ? "neg" : ""}>
                    {age === null ? "zatím žádný" : `před ${duration(age)}`}
                  </td>
                  <td className={`right num ${unit.consecutive_failures > 0 ? "neg" : ""}`}>
                    {unit.consecutive_failures}
                  </td>
                  <td>{unit.enabled ? "sbírá" : "vypnuto"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
